import type { PointOptionsObject } from "highcharts";
import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { BarChart, MeanChart, Table } from "@gss/components/icons";
import { useMessages } from "@gss/components/Messages";
import type { MeasurementStatQuestion } from "@gss/types/measurementStat";
import type { QuestionScoreChoice } from "@gss/types/statScore";
import { Chart } from "../charts/Chart";
import type { QuestionsData } from "../filters/filters.types";
import { NavButtons } from "../util/NavButtons";
import type { NavButton } from "../util/NavButtons";
import { StatsTable } from "../util/StatsTable";
import { TooFewRespondents } from "../util/TooFewRespondents";
import { useTranslation } from "../util/Translation";
import type { GetTranslation } from "../util/Translation";

interface Props {
    measurement: string;
    question: MeasurementStatQuestion;
    subtitle: string;
    series?: QuestionsData;
}

enum Tab {
    Choices = 1,
    MeanValue = 2,
    Table = 3,
}

export function Question({
    measurement,
    question,
    subtitle,
    series,
}: Props): JSX.Element | null {
    const [tab, setTab] = useState(Tab.Choices);
    const { translation, getTranslation } = useTranslation();
    const { addError } = useMessages();

    const t = translation.stats;
    const choicesTitle = t.choicesPercentage;
    const meanValueTitle = t.meanValue;

    const questionText = getTranslation(question.texts);
    const explanationText =
        question.explanationTexts != null
            ? getTranslation(question.explanationTexts)
            : undefined;
    const subtitleWithExplanation =
        explanationText != null
            ? `${explanationText}<br/><br/>${subtitle}`
            : subtitle;

    if (questionText == null) {
        console.error(`Question '${question.id}' has no text`);
        addError(`Frågan '${question.id}' saknar texter och kan ej visas`);
        return null;
    }

    const renderButtonRow = () => {
        const buttons: NavButton<Tab>[] = [
            { id: Tab.Choices, icon: BarChart, title: choicesTitle },
        ];
        if (question.hasPosChoices) {
            buttons.push({
                id: Tab.MeanValue,
                icon: MeanChart,
                title: meanValueTitle,
            });
        }
        buttons.push({ id: Tab.Table, icon: Table, title: t.tableChart });
        return (
            <NavButtons
                current={tab}
                onClick={(b) => setTab(b.id)}
                buttons={buttons}
            />
        );
    };

    const renderMeanValue = () => (
        <Chart
            key={tab}
            variant="column"
            title={questionText}
            subtitle={subtitleWithExplanation}
            yTitle={t.meanValueYAxis}
            filename={`${measurement} ${questionText} ${meanValueTitle}`}
            categories={undefined}
            series={series?.ok}
            callback={(s) => {
                if (s.question.status === "nok") {
                    return { data: [null] };
                }
                return {
                    data: [s.question.score.value],
                    confidenceInterval: s.question.score.confidenceInterval,
                };
            }}
        />
    );

    const renderChoices = () => {
        const categories = getCategories(getTranslation, question);
        const rotateDataLabels = series != null && series.ok.length > 2;
        return (
            <Chart
                key={tab}
                variant="column"
                title={questionText}
                yTitle={t.choicesPercentageYAxis}
                subtitle={subtitleWithExplanation}
                suffix="%"
                filename={`${measurement} ${questionText} ${choicesTitle}`}
                categories={categories}
                series={series?.ok}
                rotateDataLabels={rotateDataLabels}
                callback={(s) => ({
                    data: getChoicesInOrder(s.question).map((c) =>
                        getChoiceData(rotateDataLabels, c),
                    ),
                })}
            />
        );
    };

    const renderTable = () => {
        const categories = getCategories(getTranslation, question);
        return (
            <StatsTable
                className="chart-column"
                title={questionText}
                explanation={explanationText}
                subtitle={subtitle}
                categories={[...categories, t.numRespondents]}
                series={series?.ok}
                callback={(s) => [
                    ...getChoicesInOrder(s.question).map((c) => c.answers),
                    t.numRespondentsOfTotal(
                        s.question.respondents,
                        s.numRespondents,
                    ),
                ]}
            />
        );
    };

    const renderTab = () => {
        switch (tab) {
            case Tab.MeanValue:
                return renderMeanValue();
            case Tab.Choices:
                return renderChoices();
            case Tab.Table:
                return renderTable();
            default: {
                const _ensureExhaustive: never = tab;
                throw new Error("Unknown tab value");
            }
        }
    };

    return (
        <>
            <TooFewRespondents series={series?.notOk} />
            {renderButtonRow()}
            {renderTab()}
        </>
    );
}

function getChoiceData(
    rotateDataLabels: boolean,
    choice: QuestionScoreChoice,
): PointOptionsObject {
    const inside = rotateDataLabels && choice.percentageAnswers >= 80;
    return {
        y: choice.percentageAnswers,
        dataLabels: {
            inside,
            verticalAlign: inside ? "top" : "bottom",
            y: inside ? 4 : 0,
            color: inside ? "white" : "black",
        },
    };
}

function getChoicesInOrder<T extends { value: number }>(question: {
    id: string;
    choices?: T[];
}): T[] {
    if (question.choices == null) {
        console.error(`Question '${question.id}' has no choices`);
        return [];
    }
    const res = [...question.choices];
    res.sort((a, b) => a.value - b.value);
    return res;
}

function getCategories(
    getTranslation: GetTranslation,
    question: MeasurementStatQuestion,
): string[] {
    const choices = getChoicesInOrder(question);
    const includeValue = choices.some((c) => c.texts == null);

    return choices.map((c): string => {
        if (c.texts != null) {
            const text = getTranslation(c.texts);

            if (text != null) {
                if (includeValue) {
                    return `${c.value} (${text})`;
                }
                return text;
            }

            console.error(
                `Question '${question.id}' has no text for choice ${c.value}`,
            );
        }

        return String(c.value);
    });
}
